import { useMemo, useState } from "react";

import { ExternalLinkModal } from "simplydo/react/components/ExternalLinkModal";

import { RichTextEditor, type EditorProps } from "./editor";
import type { EditorCapabilities } from "./editor/types";
import { EditMenu, type ExtraMenuItem, type MenuCommands } from "./ui/EditMenu";
import { FloatingFileToolbar } from "./ui/FloatingFileToolbar";
import { FloatingLinkToolbar } from "./ui/FloatingLinkToolbar";
import Mentions from "./ui/Mentions";
import type { MentionSuggestion } from "./ui/Mentions/types";

export type EditorMenuItem = ExtraMenuItem & {
  onClick?: (attrs: {
    name: string;
    commands: MenuCommands;
    selectedText: string;
    isActive: boolean;
  }) => void;
};

export type BasicRichTextProps = {
  extraMenuItems?: Array<EditorMenuItem>;
  stickyMenu?: boolean;
  stickyMenuOffset?: number;

  hideMenu?: boolean;
  /** Shows undo/redo toolbar buttons only — TipTap history is always enabled. */
  historyEnabled?: boolean;

  // These are passed through from `RichText` and used by `Mentions`.
  mentions?: Record<string, unknown[]>;
  onSuggest?: (kind: string[], query: string) => void;
} & EditorProps;

export const BasicRichText = ({
  settings,
  capabilities = {},
  readOnly = false,
  hideMenu,
  mentions,
  extraMenuItems,
  stickyMenu,
  stickyMenuOffset,
  historyEnabled,
  onSuggest,
  ...otherProps
}: BasicRichTextProps) => {
  const [followExternalLink, setFollowExternalLink] = useState<string | null>(
    null,
  );
  const hasMentionsOrEmoji = useMemo(
    () => !!(capabilities.mentions?.length || capabilities.emoji) as boolean,
    [capabilities],
  );

  const handleExternalLink = (href: string) => {
    if (settings?.onExternalLink) {
      settings.onExternalLink(href);
      return;
    }
    setFollowExternalLink(href);
  };

  const editorSettings = { ...settings, onExternalLink: handleExternalLink };

  return (
    <>
      <ExternalLinkModal
        open={followExternalLink !== null}
        href={followExternalLink || ""}
        onClose={() => {
          setFollowExternalLink(null);
        }}
        onFollow={() => {
          setFollowExternalLink(null);
        }}
      />
      <RichTextEditor
        capabilities={capabilities}
        readOnly={!!readOnly}
        settings={editorSettings}
        {...otherProps}
      >
        {!readOnly && !hideMenu ? (
          <EditMenu
            extraMenuItems={extraMenuItems}
            sticky={!!stickyMenu}
            stickyOffset={stickyMenuOffset ?? 0}
            historyEnabled={!!historyEnabled}
          />
        ) : null}
        {!readOnly && hasMentionsOrEmoji ? (
          <Mentions
            mentions={mentions || {}}
            capabilities={capabilities as EditorCapabilities}
            fullMentionLinks={!!settings?.fullMentionLinks}
            onSuggest={onSuggest || (() => {})}
          />
        ) : null}
        {!readOnly && !hideMenu && capabilities.link ? (
          <FloatingLinkToolbar />
        ) : null}
        {!readOnly && !hideMenu && capabilities.file ? (
          <FloatingFileToolbar />
        ) : null}
      </RichTextEditor>
    </>
  );
};
